import { stickerUrl } from "@listou/shared/stickers";

import type { BlogPost } from "../../lib/blog";
import { CARDS, INLINE_CTA } from "../../lib/blog-markers";

export const listeDeNoelEnfantIdeesParAge: BlogPost = {
  h1: "Liste de Noël pour enfant : les idées par âge, de 1 à 14 ans",
  seoTitle: "Liste de Noël enfant : idées de cadeaux par âge, de 1 à 14 ans · Listou",
  seoDescription:
    "Que mettre sur la liste de Noël d'un enfant ? Les idées de cadeaux par âge, de 1 à 14 ans : éveil, imitation, construction, jeux de société, loisirs créatifs. Avec les règles pour une liste qui plaît à l'enfant et aide la famille.",
  publishedOn: "2026-10-26",
  updatedOn: "2026-10-26",
  summary:
    "Une bonne liste de Noël d'enfant suit son âge plus que la mode : l'éveil et l'empilement avant 2 ans, l'imitation entre 2 et 4 ans, la construction et les premiers jeux de société vers 5 ans, puis les loisirs et les passions. Comptez 10 à 15 envies, avec quelques petits prix pour les cousins, et faites-la avec l'enfant : il en parlera moins à tout le monde, et la famille n'aura qu'une liste à consulter.",
  body: `
## Les idées par âge, en un coup d'œil
${CARDS}
## De 1 à 2 ans : bouger, empiler, toucher

À cet âge, l'enfant découvre ce que font ses mains et ses jambes. Les cadeaux qui durent sont ceux qu'il manipule :

- **un porteur ou un trotteur**, pour les premiers pas ;
- **des cubes**, des gobelets à empiler, une boîte à formes ;
- **des livres en carton** à toucher et à soulever ;
- **un premier instrument** : tambourin, xylophone ;
- **une poupée en tissu** ou un doudou de rechange.

Vérifiez l'âge indiqué sur la boîte : les jouets marqués « ne convient pas aux enfants de moins de 36 mois » contiennent de petites pièces. Ce n'est pas une suggestion.

## De 2 à 4 ans : faire comme les grands

L'âge de l'imitation. L'enfant rejoue ce qu'il voit à la maison :

- **une cuisine ou une dînette**, une caisse enregistreuse, des fruits à couper ;
- **un établi** et des outils en bois ;
- **une poupée** avec son lit ou sa poussette ;
- **des véhicules** : garage, circuit, camion de pompier ;
- **des gros blocs de construction** et des premiers puzzles de 6 à 24 pièces ;
- **une draisienne**, vers 3 ans.

C'est aussi l'âge où les grands-parents achètent la même cuisine en bois : une liste évite d'en avoir deux.

## De 5 à 7 ans : construire et jouer ensemble

L'enfant suit une règle, construit un projet et veut gagner :

- **les jeux de construction** à notice, en petites briques ;
- **les premiers jeux de société** : mémoire, jeux de dés, jeux coopératifs ;
- **les loisirs créatifs** : perles, pâte à modeler, peinture, gommettes ;
- **les déguisements**, chevalier, princesse, pompier, avec les accessoires ;
- **un vélo** sans petites roues, souvent vers 5 ou 6 ans ;
- **les premières lectures** et les albums d'activités.

## De 8 à 11 ans : les passions

Les goûts s'affirment, et la liste se personnalise. Demandez-lui, plutôt que de deviner :

- **les jeux de société** plus longs, de stratégie ou d'enquête ;
- **les kits scientifiques** : chimie, cristaux, robots à monter, télescope ;
- **les romans et les bandes dessinées**, en précisant les tomes déjà lus ;
- **le sport** : rollers, trottinette, panier de basket, maillot de son équipe ;
- **la musique** : ukulélé, clavier, enceinte ;
- **une activité** : un stage, une entrée au parc, une séance d'escalade.
${INLINE_CTA}
## De 12 à 14 ans : moins d'objets, plus de choix

À l'adolescence, la liste raccourcit et les prix montent. Quelques pistes :

- **les vêtements et les chaussures** qu'il ou elle a choisis, avec la taille dans la note ;
- **le matériel de sa passion** : dessin, photo, musique, sport ;
- **les jeux vidéo** et accessoires, en précisant la console ;
- **les sorties** : concert, match, escape game entre amis ;
- **les cartes cadeaux**, quand rien d'autre ne marche.

Un gros cadeau, un casque ou une console, s'offre à plusieurs : sur Listou, l'envie porte une quantité, et chaque proche réserve sa part.

## Combien d'envies sur la liste d'un enfant ?

**Entre 10 et 15**, pour une famille de taille moyenne. Moins, et les derniers à ouvrir la liste ne trouvent plus rien. Plus, et l'enfant reçoit une montagne de cadeaux dont il ne jouera qu'avec trois.

Étalez les prix : quelques envies sous 15 € pour les cousins et les parrains qui ont plusieurs filleuls, des cadeaux moyens, et un ou deux plus gros à offrir à plusieurs.

## Faire la liste avec l'enfant

C'est la meilleure façon d'avoir une liste juste, et de mettre fin aux trois versions différentes racontées à chaque adulte :

- **parcourez avec lui** les catalogues ou les sites, et collez ensemble les liens de ce qui lui plaît ;
- **marquez ses coups de cœur** : sur Listou, chaque envie se marque **Coup de cœur**, **Envie** ou **Idée** ;
- **laissez-le décorer la page** : sur Listou, il choisit ses stickers, dinosaures, licornes, espace ou pirates ;
- **retirez ce qui ne lui plaît plus**, plutôt que de laisser la famille acheter un jouet dépassé.

Les plus jeunes écrivent souvent aussi une lettre au Père Noël : notre guide de la [lettre au Père Noël](/blog/lettre-au-pere-noel) explique comment faire des deux une seule et même liste.

## La surprise, même pour les enfants

L'enfant ne voit pas ce qui a été réservé sur sa liste, et les parents qui la tiennent non plus : ils savent seulement que la famille s'en occupe. Les cadeaux du Père Noël restent une surprise jusqu'au matin du 25.

Pour trouver d'autres idées, nos [idées cadeaux](/idees-cadeaux) recensent ce que les parents ajoutent à leurs listes. Et pour réunir les listes de tous les enfants derrière un seul lien, voyez les [listes en groupe](/listes-en-groupe).
`,
  faq: [
    [
      "Que mettre sur la liste de Noël d'un enfant de 3 ans ?",
      "Des jouets d'imitation (cuisine, dînette, établi, poupée), des véhicules, des gros blocs de construction, des puzzles de quelques pièces et une draisienne. Vérifiez l'âge indiqué sur la boîte pour les petites pièces.",
    ],
    [
      "Combien de cadeaux mettre sur la liste de Noël d'un enfant ?",
      "Entre 10 et 15 pour une famille de taille moyenne, avec quelques petits prix sous 15 €, des cadeaux moyens et un ou deux gros à offrir à plusieurs.",
    ],
    [
      "Quels cadeaux de Noël pour un enfant de 8 à 10 ans ?",
      "Des jeux de société de stratégie ou d'enquête, des kits scientifiques, des romans et bandes dessinées, du matériel de sport ou de musique, ou une activité comme un stage ou une sortie.",
    ],
    [
      "Comment faire une liste de Noël avec son enfant ?",
      "Parcourez ensemble les catalogues ou les sites, collez les liens de ce qui lui plaît sur une liste en ligne et marquez ses coups de cœur. Sur Listou, il choisit aussi les stickers de sa page.",
    ],
    [
      "L'enfant voit-il les cadeaux réservés sur sa liste ?",
      "Sur Listou, non, et ses parents non plus : ils savent seulement que des cadeaux ont été réservés. La surprise tient jusqu'à Noël.",
    ],
    [
      "Comment faire une liste pour plusieurs enfants ?",
      "Une liste par enfant, réunies dans un groupe : la famille ouvre un seul lien et voit la liste de chacun. Les parents tiennent les listes de leurs enfants.",
    ],
  ],
  occasion: "christmas",
  inlineCta: {
    title: "Sa liste, à son image",
    text: "Collez les liens avec lui, choisissez ses stickers, envoyez un seul lien à la famille.",
    sticker: stickerUrl("dinos", "trex"),
  },
  cards: [
    {
      title: "1 à 2 ans",
      text: "Porteur, cubes à empiler, livres en carton, premier xylophone.",
      sticker: stickerUrl("rayons-chambre", "premiers-jouets"),
    },
    {
      title: "2 à 4 ans",
      text: "Cuisine en bois, dînette, établi, poupée, garage, draisienne.",
      sticker: stickerUrl("rayons-jouets", "imitation"),
    },
    {
      title: "5 à 7 ans",
      text: "Briques à notice, premiers jeux de société, déguisements, vélo.",
      sticker: stickerUrl("rayons-jouets", "construction"),
    },
    {
      title: "8 à 11 ans",
      text: "Jeux de stratégie, kits scientifiques, romans, sport, musique.",
      sticker: stickerUrl("espace", "fusee"),
    },
    {
      title: "12 à 14 ans",
      text: "Vêtements choisis, matériel de passion, sorties, un gros cadeau à plusieurs.",
      sticker: stickerUrl("musique", "casque"),
    },
    {
      title: "Pour tous les âges",
      text: "Des livres, une activité à faire ensemble, et un petit prix pour les cousins.",
      sticker: stickerUrl("chateau", "livre"),
    },
  ],
};
